import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import cn from 'classnames';
import ClassAttendanceDetails from 'components/Course/ClassAttendanceDetails';
import * as dt from 'core/datetime';
import { apiErrorHandler } from 'core/errors';
import { DATE_FORMAT } from 'globals/constants';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { pluralize } from 'utils';

import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';
import { ReactComponent as CheckIcon } from 'assets/green-check.svg';

interface GetAllClassAttendancesResponse extends MessageResponse {
  classData: ClassAverageAttendance[];
}

interface AllClassesAttendanceProps extends ToastManagerProp {
  getAccess: SessionData | null;
}

interface AllClassesAttendanceState {
  isLoading: boolean;
  sessions: ClassAverageAttendance[];
  showClassAttendanceDetails: boolean;
  classBeingViewed: ClassAverageAttendance | null;
}

class AllClassesAttendance extends Component<AllClassesAttendanceProps, AllClassesAttendanceState> {
  apiSources = getCancelToken();

  state: AllClassesAttendanceState = {
    sessions: [],
    isLoading: false,
    classBeingViewed: null,
    showClassAttendanceDetails: false,
  };

  async getAllClassAttendances(courseId: string) {
    const response = await request<GetAllClassAttendancesResponse>({
      url: API.ATTENDANCE_COURSE_ALL,
      params: { courseId },
      cancelToken: this.apiSources.token,
    });
    return response.classData.sort((c1, c2) => c1.classNum - c2.classNum);
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      const getAccess = this.props.getAccess;
      if (getAccess?.courseDetails.courseId) {
        this.setState({ isLoading: true });
        const sessions = await this.getAllClassAttendances(getAccess.courseDetails.courseId);
        this.setState({ sessions, isLoading: false });
      }
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  showClassAttendanceDetails = (classToView: ClassAverageAttendance) => {
    if (classToView.classNum <= 0) return;
    this.setState({
      showClassAttendanceDetails: true,
      classBeingViewed: classToView,
    });
  };

  hideClassAttendanceDetails = (hasExported: boolean) => {
    this.setState({
      showClassAttendanceDetails: false,
    });

    this.setState({
      sessions: this.state.sessions.map((session) => {
        if (this.state.classBeingViewed?.classId === session.classId) {
          if (hasExported) {
            return {
              ...session,
              exported: 1,
            };
          }
          return {
            ...session,
            exported: 0,
          };
        }
        return session;
      }),
    });
  };

  getRow = (session: ClassAverageAttendance) => {
    const { attendanceRecorded } = session;
    return (
      <tr
        key={session.classId}
        className={cn('backgroundColorWhite', 'clickable')}
        onClick={() => this.showClassAttendanceDetails(session)}
      >
        <td className='table__cell'>
          <div className='table__cell-header table__cell-header--title'>
            {session.classNum < 0 ? 'Acadly not used' : `Session ${session.classNum}`}
          </div>
          <div className='table__cell-value table__cell-value--sm'>{session.title}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>HELD ON</div>
          <div className='table__cell-value table__cell-value--no-wrap'>
            {dt.format(session.startTime, DATE_FORMAT)}
          </div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>STUDENTS PRESENT</div>
          <div className='table__cell-value'>{session.numPresent}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>ATTENDANCE</div>
          {session.attendanceRecorded ? (
            <div className={cn('table__cell-value', getColorClassForPercentage(session.averageAttendance))}>
              {session.averageAttendance}%
            </div>
          ) : (
            <div className='table__cell-value'>--</div>
          )}
        </td>
        <td className='has-tooltip'>
          {!attendanceRecorded ? null : session.exported ? (
            <div className='table__cell tooltip-trigger left' data-tooltip='Already exported'>
              <CheckIcon display='block' />
            </div>
          ) : (
            <div className='table__cell pointer tooltip-trigger left' data-tooltip='Export Attendance'>
              <ChevronRightIcon display='block' />
            </div>
          )}
        </td>
      </tr>
    );
  };

  render() {
    const { isLoading, sessions, classBeingViewed, showClassAttendanceDetails } = this.state;
    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {isLoading
              ? 'Loading attendance...'
              : !sessions.length
              ? 'Attendance not recorded in any session yet'
              : `Attendance records available for ${sessions.length} ${pluralize(
                  sessions.length,
                  'session'
                )}`}
          </h3>
        </div>
        <div className='section__body'>
          <table className='table table--bordered'>
            <tbody>{this.state.sessions.map(this.getRow)}</tbody>
          </table>
        </div>
        <ClassAttendanceDetails
          isOpen={showClassAttendanceDetails}
          session={classBeingViewed}
          onClose={this.hideClassAttendanceDetails}
        />
      </div>
    );
  }
}

/**
 * Returns a color class name for a number between 0 and 100, returns empty string otherwise
 * @param percentage attendance percentage
 */
const getColorClassForPercentage = (percentage: number) => {
  if (percentage >= 80) return 'table__cell-value--green';
  else if (percentage >= 60) return 'table__cell-value--orange';
  return 'table__cell-value--red';
};

const mapStateToProps = (state: RootState) => {
  return {
    getAccess: state.getAccess,
  };
};

export default connect(mapStateToProps)(withToastManager(AllClassesAttendance));
